/**
 * Gallery Page Component
 * Protected page with folder-first gallery experience
 * Landing shows folder cards; clicking a folder shows its photos with upload
 * Supports bulk upload with parallel concurrency
 *
 * @module pages/GalleryPage
 */

import { useEffect, useState } from 'react'
import { logger } from '@/utils/logger'
import { Layout, ProtectedRoute } from '@/components/layout'
import { PhotoGrid, PhotoUploader, PhotoLightbox, FolderGrid } from '@/components/gallery'
import { useAuth } from '@/hooks/useAuth'
import * as photoService from '@/services/photoService'
import type { Photo, Album, UploadProgress } from '@/types'

// ============================================================================
// TYPES
// ============================================================================

/** View mode state machine for gallery navigation */
type GalleryViewMode = 'folders' | 'album' | 'all';

/** Concurrency limit for parallel bulk uploads */
const UPLOAD_CONCURRENCY = 3;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Gallery page component with folder-first navigation
 * - 'folders' mode (default): shows folder grid with create + all photos tiles
 * - 'album' mode: shows photos for a specific folder with upload
 * - 'all' mode: shows all photos across all folders with upload
 */
export default function GalleryPage() {
  const auth = useAuth()

  // View mode state
  const [viewMode, setViewMode] = useState<GalleryViewMode>('folders')
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null)

  // Data state
  const [photos, setPhotos] = useState<Photo[]>([])
  const [albums, setAlbums] = useState<Album[]>([])

  // Loading & error state
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([])
  const [error, setError] = useState<string>('')

  // Lightbox state
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | undefined>()
  const [movingPhotoId, setMovingPhotoId] = useState<string | undefined>()

  // Create folder state
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderDescription, setNewFolderDescription] = useState('')
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)

  logger.debug('[WALRUS] GalleryPage: rendering', {
    viewMode,
    userId: auth.user?.id,
    photoCount: photos.length,
    albumCount: albums.length,
    selectedAlbumId: selectedAlbum?.id,
  })

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================

  /**
   * Load albums on mount (always needed for folder grid)
   */
  useEffect(() => {
    const loadAlbums = async () => {
      logger.debug('[WALRUS] GalleryPage: loading albums')
      try {
        const albumsData = await photoService.getAlbums()
        logger.debug('[WALRUS] GalleryPage: loaded albums', { count: albumsData.length })
        setAlbums(albumsData)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load albums'
        logger.error('[WALRUS] GalleryPage: error loading albums', { error: errorMessage })
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    loadAlbums()
  }, [])

  /**
   * Load photos when entering album or all-photos mode
   */
  useEffect(() => {
    if (viewMode === 'folders') {
      // No photos needed in folder view
      setPhotos([])
      return
    }

    const loadPhotos = async () => {
      logger.debug('[WALRUS] GalleryPage: loading photos', {
        viewMode,
        albumId: selectedAlbum?.id,
      })
      setIsLoading(true)
      setError('')

      try {
        const albumId = viewMode === 'album' ? selectedAlbum?.id : undefined
        const photosData = await photoService.getPhotos(albumId)
        logger.debug('[WALRUS] GalleryPage: loaded photos', { count: photosData.items.length })
        setPhotos(photosData.items)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load photos'
        logger.error('[WALRUS] GalleryPage: error loading photos', { error: errorMessage })
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    loadPhotos()
  }, [viewMode, selectedAlbum?.id])

  // ==========================================================================
  // NAVIGATION
  // ==========================================================================

  /** Navigate into a folder */
  const handleFolderClick = (album: Album) => {
    logger.debug('[WALRUS] GalleryPage: navigating into folder', {
      albumId: album.id,
      albumName: album.name,
    })
    setSelectedAlbum(album)
    setViewMode('album')
    setError('')
  }

  /** Navigate to all-photos view */
  const handleViewAllPhotos = () => {
    logger.debug('[WALRUS] GalleryPage: navigating to all photos view')
    setSelectedAlbum(null)
    setViewMode('all')
    setError('')
  }

  /** Navigate back to folder grid */
  const handleBackToFolders = () => {
    logger.debug('[WALRUS] GalleryPage: navigating back to folders')
    setViewMode('folders')
    setSelectedAlbum(null)
    setPhotos([])
    setError('')

    // Refresh albums to get updated counts/covers
    const refreshAlbums = async () => {
      try {
        const albumsData = await photoService.getAlbums()
        setAlbums(albumsData)
      } catch (err) {
        logger.error('[WALRUS] GalleryPage: error refreshing albums', {
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }
    refreshAlbums()
  }

  // ==========================================================================
  // FOLDER CREATION
  // ==========================================================================

  /** Handle creating a new folder */
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name is required')
      return
    }

    logger.debug('[WALRUS] GalleryPage: creating folder', {
      name: newFolderName,
      description: newFolderDescription,
    })

    setIsCreatingFolder(true)
    setError('')

    try {
      const newAlbum = await photoService.createAlbum(
        newFolderName.trim(),
        newFolderDescription.trim() || undefined
      )
      logger.info('[WALRUS] GalleryPage: folder created successfully', {
        albumId: newAlbum.id,
        name: newAlbum.name,
      })

      // Add to albums list and navigate into it
      setAlbums((prev) => [newAlbum, ...prev])
      setShowCreateFolder(false)
      setNewFolderName('')
      setNewFolderDescription('')

      // Navigate into the new folder
      setSelectedAlbum(newAlbum)
      setViewMode('album')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create folder'
      logger.error('[WALRUS] GalleryPage: create folder error', { error: errorMessage })
      setError(errorMessage)
    } finally {
      setIsCreatingFolder(false)
    }
  }

  // ==========================================================================
  // PHOTO UPLOAD (BULK WITH PARALLEL CONCURRENCY)
  // ==========================================================================

  /**
   * Handle files selected for upload with parallel bulk upload
   * Processes files in chunks of UPLOAD_CONCURRENCY for performance
   */
  const handleFilesSelected = async (files: File[]) => {
    // Determine target album - in album mode, always use selected album
    const targetAlbumId = viewMode === 'album' ? selectedAlbum?.id : undefined

    logger.debug('[WALRUS] GalleryPage: files selected for upload', {
      count: files.length,
      albumId: targetAlbumId,
      concurrency: UPLOAD_CONCURRENCY,
    })

    setIsUploading(true)
    setError('')

    const uploadedPhotos: Photo[] = []

    try {
      // Process files in chunks for parallel upload with concurrency limit
      for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
        const chunk = files.slice(i, i + UPLOAD_CONCURRENCY)
        logger.debug('[WALRUS] GalleryPage: uploading chunk', {
          chunkIndex: Math.floor(i / UPLOAD_CONCURRENCY),
          chunkSize: chunk.length,
          totalFiles: files.length,
        })

        const results = await Promise.allSettled(
          chunk.map((file) =>
            photoService.uploadPhoto(
              file,
              targetAlbumId,
              (progress) => {
                logger.debug('[WALRUS] GalleryPage: upload progress', {
                  filename: progress.filename,
                  progress: progress.progress,
                })
                setUploadProgress((prev) => {
                  const existing = prev.find((p) => p.filename === progress.filename)
                  if (existing) {
                    return prev.map((p) =>
                      p.filename === progress.filename ? progress : p
                    )
                  }
                  return [...prev, progress]
                })
              }
            )
          )
        )

        // Collect successful uploads
        results.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            uploadedPhotos.push(result.value)
            logger.info('[WALRUS] GalleryPage: file uploaded successfully', {
              photoId: result.value.id,
              filename: chunk[idx].name,
            })
          } else {
            logger.error('[WALRUS] GalleryPage: file upload failed', {
              filename: chunk[idx].name,
              error: result.reason instanceof Error ? result.reason.message : String(result.reason),
            })
          }
        })
      }

      // Add uploaded photos to the gallery
      if (uploadedPhotos.length > 0) {
        logger.debug('[WALRUS] GalleryPage: adding uploaded photos to gallery', {
          count: uploadedPhotos.length,
        })
        setPhotos((prev) => [...uploadedPhotos, ...prev])

        // Update selected album count in local state
        if (selectedAlbum) {
          setSelectedAlbum((prev) =>
            prev
              ? { ...prev, photoCount: prev.photoCount + uploadedPhotos.length }
              : null
          )
          setAlbums((prev) =>
            prev.map((a) =>
              a.id === selectedAlbum.id
                ? { ...a, photoCount: a.photoCount + uploadedPhotos.length }
                : a
            )
          )
        }
      }

      // Clear upload progress after a delay
      setTimeout(() => {
        setUploadProgress([])
      }, 2000)

      if (uploadedPhotos.length < files.length) {
        const failedCount = files.length - uploadedPhotos.length
        setError(`${failedCount} file(s) failed to upload. Please try again.`)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed'
      logger.error('[WALRUS] GalleryPage: upload error', { error: errorMessage })
      setError(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  // ==========================================================================
  // PHOTO DELETION
  // ==========================================================================

  /** Handle photo deletion */
  const handleDeletePhoto = async (photoId: string) => {
    logger.debug('[WALRUS] GalleryPage: deleting photo', { photoId })

    setDeletingPhotoId(photoId)
    setError('')

    try {
      await photoService.deletePhoto(photoId)
      logger.info('[WALRUS] GalleryPage: photo deleted successfully', { photoId })

      // Remove from photos list
      setPhotos((prev) => prev.filter((p) => p.id !== photoId))

      // Update album count in local state
      if (selectedAlbum) {
        setSelectedAlbum((prev) =>
          prev ? { ...prev, photoCount: Math.max(0, prev.photoCount - 1) } : null
        )
        setAlbums((prev) =>
          prev.map((a) =>
            a.id === selectedAlbum.id
              ? { ...a, photoCount: Math.max(0, a.photoCount - 1) }
              : a
          )
        )
      }

      // Close lightbox if viewing deleted photo
      if (selectedPhoto?.id === photoId) {
        setSelectedPhoto(null)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete photo'
      logger.error('[WALRUS] GalleryPage: delete error', { error: errorMessage })
      setError(errorMessage)
    } finally {
      setDeletingPhotoId(undefined)
    }
  }

  // ==========================================================================
  // PHOTO MOVE
  // ==========================================================================

  /** Handle moving a photo to a different album */
  const handleMovePhoto = async (photoId: string, targetAlbumId: string | null) => {
    logger.debug('[WALRUS] GalleryPage: moving photo', { photoId, targetAlbumId })

    setMovingPhotoId(photoId)
    setError('')

    try {
      const updatedPhoto = await photoService.movePhoto(photoId, targetAlbumId)
      logger.info('[WALRUS] GalleryPage: photo moved successfully', {
        photoId,
        targetAlbumId,
      })

      // Find the source album from the photo's current albumId before the move
      const sourceAlbumId = photos.find((p) => p.id === photoId)?.albumId || null

      // Update photo in local state with new albumId
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId ? { ...p, albumId: updatedPhoto.albumId } : p
        )
      )

      // In album view: remove the photo from grid if it was moved to a different album
      if (viewMode === 'album' && selectedAlbum) {
        if (targetAlbumId !== selectedAlbum.id) {
          logger.debug('[WALRUS] GalleryPage: removing moved photo from current album view', {
            photoId,
          })
          setPhotos((prev) => prev.filter((p) => p.id !== photoId))
        }
      }

      // Update album counts in local state
      // Decrement source album count
      if (sourceAlbumId) {
        setAlbums((prev) =>
          prev.map((a) =>
            a.id === sourceAlbumId
              ? { ...a, photoCount: Math.max(0, a.photoCount - 1) }
              : a
          )
        )
        // Also update selectedAlbum if it's the source
        if (selectedAlbum?.id === sourceAlbumId) {
          setSelectedAlbum((prev) =>
            prev ? { ...prev, photoCount: Math.max(0, prev.photoCount - 1) } : null
          )
        }
      }

      // Increment target album count
      if (targetAlbumId) {
        setAlbums((prev) =>
          prev.map((a) =>
            a.id === targetAlbumId
              ? { ...a, photoCount: a.photoCount + 1 }
              : a
          )
        )
        // Also update selectedAlbum if it's the target
        if (selectedAlbum?.id === targetAlbumId) {
          setSelectedAlbum((prev) =>
            prev ? { ...prev, photoCount: prev.photoCount + 1 } : null
          )
        }
      }

      // Close lightbox after successful move
      setSelectedPhoto(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to move photo'
      logger.error('[WALRUS] GalleryPage: move error', { error: errorMessage })
      setError(errorMessage)
    } finally {
      setMovingPhotoId(undefined)
    }
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <ProtectedRoute>
      <Layout contentClassName="bg-walrus-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">

          {/* Breadcrumb Navigation */}
          {viewMode !== 'folders' && (
            <nav className="flex items-center space-x-2 text-sm">
              <button
                onClick={handleBackToFolders}
                className="text-walrus-silver hover:text-walrus-black transition-colors font-medium"
                type="button"
              >
                Gallery
              </button>
              <svg
                className="h-4 w-4 text-walrus-gray"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="text-walrus-black font-medium">
                {viewMode === 'album' ? selectedAlbum?.name : 'All Photos'}
              </span>
            </nav>
          )}

          {/* Header */}
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold text-walrus-black">
              {viewMode === 'folders'
                ? 'Family Photo Gallery'
                : viewMode === 'album'
                  ? selectedAlbum?.name || 'Folder'
                  : 'All Photos'}
            </h1>
            <p className="text-walrus-silver text-lg">
              {viewMode === 'folders'
                ? 'Browse our collection of family memories and adventures'
                : viewMode === 'album'
                  ? `${selectedAlbum?.photoCount || 0} photos${selectedAlbum?.description ? ` - ${selectedAlbum.description}` : ''}`
                  : 'All photos across all folders'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
              <p className="font-semibold">Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* ================================================================ */}
          {/* FOLDERS VIEW */}
          {/* ================================================================ */}
          {viewMode === 'folders' && (
            <>
              {/* Create Folder Form (inline) */}
              {showCreateFolder && (
                <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-6 space-y-4">
                  <h2 className="text-xl font-bold text-walrus-black">
                    Create New Folder
                  </h2>

                  <div>
                    <label htmlFor="folder-name" className="block text-sm font-medium text-walrus-black mb-1">
                      Folder Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="folder-name"
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      maxLength={100}
                      placeholder="e.g. Beach Holiday 2026"
                      className="w-full rounded-lg bg-walrus-white border border-walrus-gray text-walrus-black px-3 py-2 transition-colors hover:border-walrus-silver focus:outline-none focus:ring-2 focus:ring-walrus-black"
                      disabled={isCreatingFolder}
                      autoFocus
                    />
                    <p className="mt-1 text-xs text-walrus-silver">
                      {newFolderName.length}/100 characters
                    </p>
                  </div>

                  <div>
                    <label htmlFor="folder-description" className="block text-sm font-medium text-walrus-black mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      id="folder-description"
                      value={newFolderDescription}
                      onChange={(e) => setNewFolderDescription(e.target.value)}
                      placeholder="A brief description of this folder..."
                      rows={2}
                      className="w-full rounded-lg bg-walrus-white border border-walrus-gray text-walrus-black px-3 py-2 transition-colors hover:border-walrus-silver focus:outline-none focus:ring-2 focus:ring-walrus-black resize-none"
                      disabled={isCreatingFolder}
                    />
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleCreateFolder}
                      disabled={isCreatingFolder || !newFolderName.trim()}
                      className="rounded-lg bg-walrus-black text-walrus-white px-4 py-2 text-sm font-medium transition-colors hover:bg-walrus-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                      type="button"
                    >
                      {isCreatingFolder ? 'Creating...' : 'Create Folder'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateFolder(false)
                        setNewFolderName('')
                        setNewFolderDescription('')
                        setError('')
                      }}
                      disabled={isCreatingFolder}
                      className="rounded-lg bg-walrus-white text-walrus-black border border-walrus-gray px-4 py-2 text-sm font-medium transition-colors hover:bg-walrus-cream disabled:opacity-50"
                      type="button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Folder Grid */}
              <FolderGrid
                albums={albums}
                currentUser={auth.user}
                onFolderClick={handleFolderClick}
                onCreateFolder={() => setShowCreateFolder(true)}
                onViewAllPhotos={handleViewAllPhotos}
                isLoading={isLoading}
              />
            </>
          )}

          {/* ================================================================ */}
          {/* ALBUM VIEW */}
          {/* ================================================================ */}
          {viewMode === 'album' && selectedAlbum && (
            <>
              {/* Back button */}
              <button
                onClick={handleBackToFolders}
                className="inline-flex items-center space-x-2 text-sm font-medium text-walrus-silver hover:text-walrus-black transition-colors"
                type="button"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span>Back to Folders</span>
              </button>

              {/* Photo Uploader - album pre-selected, no album dropdown */}
              <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
                <h2 className="text-2xl font-bold text-walrus-black mb-6">
                  Upload to {selectedAlbum.name}
                </h2>
                <PhotoUploader
                  onFilesSelected={handleFilesSelected}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  albums={[]}
                  selectedAlbumId={selectedAlbum.id}
                />
              </div>

              {/* Photo Grid */}
              <div>
                <h2 className="text-2xl font-bold text-walrus-black mb-6">
                  Photos ({selectedAlbum.photoCount})
                </h2>
                <PhotoGrid
                  photos={photos}
                  currentUser={auth.user}
                  onPhotoClick={setSelectedPhoto}
                  onDeletePhoto={handleDeletePhoto}
                  isLoading={isLoading}
                  isEmpty={photos.length === 0 && !isLoading}
                  deletingPhotoId={deletingPhotoId}
                />
              </div>
            </>
          )}

          {/* ================================================================ */}
          {/* ALL PHOTOS VIEW */}
          {/* ================================================================ */}
          {viewMode === 'all' && (
            <>
              {/* Back button */}
              <button
                onClick={handleBackToFolders}
                className="inline-flex items-center space-x-2 text-sm font-medium text-walrus-silver hover:text-walrus-black transition-colors"
                type="button"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span>Back to Folders</span>
              </button>

              {/* Photo Uploader - with album dropdown for organizing */}
              <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
                <h2 className="text-2xl font-bold text-walrus-black mb-6">
                  Share Your Photos
                </h2>
                <PhotoUploader
                  onFilesSelected={handleFilesSelected}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  albums={albums}
                />
              </div>

              {/* Photo Grid */}
              <div>
                <h2 className="text-2xl font-bold text-walrus-black mb-6">
                  All Photos
                </h2>
                <PhotoGrid
                  photos={photos}
                  currentUser={auth.user}
                  onPhotoClick={setSelectedPhoto}
                  onDeletePhoto={handleDeletePhoto}
                  isLoading={isLoading}
                  isEmpty={photos.length === 0 && !isLoading}
                  deletingPhotoId={deletingPhotoId}
                />
              </div>
            </>
          )}

          {/* Photo Lightbox - works from both album and all-photos views */}
          <PhotoLightbox
            isOpen={!!selectedPhoto}
            photo={selectedPhoto}
            currentUser={auth.user}
            onClose={() => setSelectedPhoto(null)}
            onDelete={handleDeletePhoto}
            onMove={handleMovePhoto}
            albums={albums}
            isDeleting={deletingPhotoId === selectedPhoto?.id}
            isMoving={movingPhotoId === selectedPhoto?.id}
          />
        </div>

        {/* TODO: Add infinite scroll pagination for photos */}
        {/* TODO: Add photo search functionality */}
        {/* TODO: Add photo tagging system */}
        {/* TODO: Add favorites/rating system */}
        {/* TODO: Add photo download for owner/admin */}
        {/* TODO: Add folder edit/delete capabilities */}
        {/* TODO: Add folder sharing/permissions */}
      </Layout>
    </ProtectedRoute>
  )
}
